import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FormInput } from '@podus/ui';
import { LuSearch } from 'react-icons/lu';
import { MdClose } from 'react-icons/md';
import { api } from '@/lib/api';
import type { StaffMember } from '@/types';
import { SuccessPanel } from './shared';

/* ─── Assign Mediator Modal ─── */

type AssignView = 'select' | 'success';

interface AssignMediatorModalProps {
    open: boolean;
    caseId: number;
    onClose: () => void;
    onAssigned?: () => void;
}

export function AssignMediatorModal({ open, caseId, onClose, onAssigned }: AssignMediatorModalProps) {
    const [view, setView] = useState<AssignView>('select');
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<number | null>(null);
    const [mediators, setMediators] = useState<StaffMember[]>([]);
    const [isAssigning, setIsAssigning] = useState(false);

    useEffect(() => {
        if (!open) return;
        api.get<StaffMember[]>('/api/staff/mediators')
            .then((res) => setMediators(res.response.data ?? []))
            .catch(() => {});
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') handleClose();
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open]);

    const handleClose = () => {
        setView('select');
        setSearch('');
        setSelected(null);
        onClose();
    };

    const handleAssign = async () => {
        if (!selected) return;
        setIsAssigning(true);
        try {
            await api.patch(`/api/cases/${caseId}/assign-mediator`, { mediatorStaffId: selected });
            setView('success');
            onAssigned?.();
        } catch {
            // keep modal open on error
        } finally {
            setIsAssigning(false);
        }
    };

    const filtered = mediators.filter((m) => m.name.toLowerCase().includes(search.toLowerCase()));

    if (!open) return null;

    return createPortal(
        <>
            <div className="fixed inset-0 z-60 bg-black/40" onClick={handleClose} />

            <div className="fixed inset-0 z-61 flex items-center justify-center p-4">
                <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
                    {view === 'select' ? (
                        <div className="flex flex-col">
                            <div className="flex items-center justify-between px-6 pt-6 pb-4">
                                <h2 className="text-lg font-bold text-secondary">Assign To Mediator</h2>
                                <button
                                    type="button"
                                    title="Close"
                                    onClick={handleClose}
                                    className="flex h-8 w-8 items-center justify-center rounded-full text-[#555] transition-colors hover:bg-[#f5f5f5]"
                                >
                                    <MdClose size={20} />
                                </button>
                            </div>

                            <div className="px-6">
                                <div className="relative">
                                    <FormInput
                                        placeholder="Search Mediator"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        variant="white"
                                    />
                                    <LuSearch
                                        size={16}
                                        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#999]"
                                    />
                                </div>
                            </div>

                            <div className="mt-3 max-h-72 overflow-y-auto px-6">
                                {filtered.length === 0 ? (
                                    <p className="py-4 text-center text-[12px] text-[#999]">No mediators found</p>
                                ) : (
                                    filtered.map((mediator) => (
                                        <label
                                            key={mediator.id}
                                            className="flex cursor-pointer items-center gap-3 py-2.5 text-[13px] text-secondary"
                                        >
                                            <input
                                                type="radio"
                                                name="mediator"
                                                checked={selected === mediator.id}
                                                onChange={() => setSelected(mediator.id)}
                                                className="sr-only"
                                            />
                                            <div
                                                className={`flex h-4 w-4 items-center justify-center rounded-full border transition-all ${
                                                    selected === mediator.id ? 'border-primary' : 'border-[#ccc]'
                                                }`}
                                            >
                                                <div
                                                    className={`h-2 w-2 rounded-full transition-all ${
                                                        selected === mediator.id
                                                            ? 'bg-primary scale-100'
                                                            : 'bg-transparent scale-0'
                                                    }`}
                                                />
                                            </div>
                                            {mediator.name}
                                        </label>
                                    ))
                                )}
                            </div>

                            <div className="px-6 pb-6 pt-4">
                                <button
                                    type="button"
                                    onClick={handleAssign}
                                    disabled={!selected || isAssigning}
                                    className="flex w-full items-center justify-center rounded-xl bg-secondary py-4 text-[13px] font-bold text-white transition-all hover:bg-secondary-400 disabled:opacity-40"
                                >
                                    {isAssigning ? 'Assigning…' : 'Assign'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <SuccessPanel
                            title="Case Assigned Successfully!"
                            message="The mediator has been notified and will begin work on the case shortly."
                            gradientId="assign-grad"
                            onClose={handleClose}
                        />
                    )}
                </div>
            </div>
        </>,
        document.body,
    );
}
